import { useState } from 'react';
import { api } from '@/api/client';
import { AppIcon } from '@/components/AppIcon';
import { ActionBar, DataGrid, StatusBar, type GridColumn } from '@/components/desktop';
import { ReverseButton } from '@/components/documents';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  FormPanel,
  OkNote,
  ScreenWithForm,
  Select,
  StatusPill,
  Table,
  TextArea,
  Tile,
  errorMessage,
  todayIso,
} from '@/components/ui';
import { formatDate, formatMoney } from '@/format';
import {
  useCashBoxes,
  useCashInAccounts,
  useCashPage,
  useCashVouchers,
  useExpenseCategories,
  useFinanceMutation,
  usePostableAccounts,
  type VoucherRow,
} from '@/hooks/finance';
import { useAuth } from '@/stores/auth';
import { useFarmScope } from '@/stores/farmScope';

/** The first of the current month, which is the page a person usually wants. */
function monthStart(): string {
  return `${todayIso().slice(0, 7)}-01`;
}

/**
 * Financials › Cash Book.
 *
 * The khata, one page per cash box, with money in, money out and the running cash
 * in hand down the right. The running figure is worked out as the page is drawn,
 * never stored — which is what makes it right after somebody writes up a voucher
 * for last Tuesday.
 *
 * Topping a farm's cash up out of head office is refused here and sent to a
 * transfer, so the same movement cannot be written down twice, once at each end.
 */
export function CashBook() {
  const can = useAuth((s) => s.can);
  const farmIds = useFarmScope((s) => s.selectedFarmIds);

  const [cashBoxId, setCashBoxId] = useState('');
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(todayIso());
  const [selected, setSelected] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<VoucherRow | null>(null);
  const [direction, setDirection] = useState<'IN' | 'OUT'>('OUT');
  const [voucherDate, setVoucherDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [counterAccountCode, setCounterAccountCode] = useState('');
  const [payee, setPayee] = useState('');
  const [voucherNo, setVoucherNo] = useState('');
  const [narration, setNarration] = useState('');
  const [error, setError] = useState('');
  const [note, setNote] = useState('');

  const boxes = useCashBoxes(farmIds);
  // One box is the normal case, so it is chosen rather than left for a click.
  // Derived during render rather than set from an effect: an effect here would
  // start a second render for a value that was already knowable in the first.
  const chosenBoxId = cashBoxId || (boxes.data?.items[0]?.id ?? '');
  const page = useCashPage(chosenBoxId, from, to);
  const vouchers = useCashVouchers({ cashBoxId: chosenBoxId, take: 200, skip: 0 });
  const categories = useExpenseCategories();
  const inAccounts = useCashInAccounts();
  const postable = usePostableAccounts();
  const mutate = useFinanceMutation<unknown>();


  const rows = vouchers.data?.items ?? [];
  const current = rows.find((r) => r.id === selected) ?? null;

  /** Only the four accounts money may come in from, named properly. */
  const inOptions = (inAccounts.data?.codes ?? []).map((code) => {
    const account = (postable.data?.items ?? []).find((a) => a.code === code);
    return { value: code, label: account ? `${code} — ${account.nameEn}` : code };
  });

  const spendable = (categories.data?.items ?? []).filter((c) => !c.isStockPurchase);

  function reset() {
    setEditing(null);
    setDirection('OUT');
    setVoucherDate(todayIso());
    setAmount('');
    setCategoryId('');
    setCounterAccountCode('');
    setPayee('');
    setVoucherNo('');
    setNarration('');
    setError('');
    setNote('');
  }

  function startEdit(row: VoucherRow) {
    setEditing(row);
    setDirection(row.direction);
    setVoucherDate(row.voucherDate);
    setAmount(row.entryAmount);
    setCategoryId(row.categoryId ?? '');
    setCounterAccountCode(row.counterAccountCode ?? '');
    setPayee(row.payee ?? '');
    setVoucherNo(row.voucherNo ?? '');
    setNarration(row.narration ?? '');
    setError('');
    setShowForm(true);
  }

  async function save() {
    setError('');
    setNote('');
    if (!chosenBoxId) return setError('Pick the cash box');
    if (!amount || Number(amount) <= 0) return setError('Enter the amount');
    if (direction === 'OUT' && !categoryId) {
      return setError('Say what the money was spent on');
    }
    if (direction === 'IN' && !counterAccountCode) {
      return setError('Say where the money came from');
    }

    const body = {
      cashBoxId: chosenBoxId,
      direction,
      voucherDate,
      amount,
      ...(direction === 'OUT' ? { categoryId } : { counterAccountCode }),
      ...(payee.trim() ? { payee: payee.trim() } : {}),
      ...(voucherNo.trim() ? { voucherNo: voucherNo.trim() } : {}),
      ...(narration.trim() ? { narration: narration.trim() } : {}),
    };

    try {
      if (editing) {
        await mutate.mutateAsync(() =>
          api.patch(`/api/v1/fin/cash-vouchers/${editing.id}`, {
            version: editing.version,
            ...body,
          }),
        );
      } else {
        await mutate.mutateAsync(() => api.post('/api/v1/fin/cash-vouchers', body));
      }
      setNote(editing ? 'Voucher saved.' : 'Voucher written.');
      setShowForm(false);
      reset();
    } catch (e) {
      // The stock-purchase refusal lands here, and it says to use Stock IN instead.
      setError(errorMessage(e, 'That voucher could not be saved'));
    }
  }

  async function post(row: VoucherRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.post(`/api/v1/fin/cash-vouchers/${row.id}/post`, { version: row.version }),
      );
      setNote(`${row.documentNo} posted.`);
    } catch (e) {
      setError(errorMessage(e, 'That voucher could not be posted'));
    }
  }

  async function remove(row: VoucherRow) {
    setError('');
    try {
      await mutate.mutateAsync(() =>
        api.delete(`/api/v1/fin/cash-vouchers/${row.id}?version=${row.version}`),
      );
      setNote('Draft deleted.');
      setSelected(null);
    } catch (e) {
      setError(errorMessage(e, 'That draft could not be deleted'));
    }
  }

  const columns: Array<GridColumn<VoucherRow>> = [
    { key: 'voucherDate', header: 'Date', width: 104, render: (r) => formatDate(r.voucherDate) },
    { key: 'documentNo', header: 'Number', width: 136, render: (r) => r.documentNo },
    {
      key: 'voucherNo',
      header: 'Paper voucher',
      width: 120,
      render: (r) => r.voucherNo ?? <span className="muted">—</span>,
    },
    {
      key: 'direction',
      header: 'In or out',
      width: 100,
      render: (r) => (r.direction === 'IN' ? 'Money in' : 'Money out'),
    },
    {
      key: 'entryAmount',
      header: 'Amount',
      width: 120,
      align: 'end',
      render: (r) => formatMoney(r.entryAmount),
    },
    {
      key: 'categoryName',
      header: 'For',
      width: 160,
      render: (r) =>
        r.categoryName ?? r.counterAccountName ?? <span className="muted">—</span>,
    },
    {
      key: 'payee',
      header: 'Payee',
      width: 150,
      render: (r) => r.payee ?? <span className="muted">—</span>,
    },
    { key: 'status', header: 'Status', width: 120, render: (r) => <StatusPill status={r.status} /> },
  ];

  const form = (
    <FormPanel
      title={editing ? `Voucher ${editing.documentNo}` : 'Write a voucher'}
      icon={<AppIcon kind="cash" size={18} />}
      intro="A page of the khata: money in or money out of one cash box."
    >
      <FormGroup title="The voucher" columns={2}>
        <Select
          label="In or out"
          value={direction}
          onChange={(v) => setDirection(v as 'IN' | 'OUT')}
          options={[
            { value: 'OUT', label: 'Money out' },
            { value: 'IN', label: 'Money in' },
          ]}
          required
          disabled={Boolean(editing)}
        />
        <Field label="Date" type="date" value={voucherDate} onChange={setVoucherDate} required />
        <Field label="Amount" type="number" value={amount} onChange={setAmount} required />
        {direction === 'OUT' ? (
          <Select
            label="What for"
            value={categoryId}
            onChange={setCategoryId}
            options={spendable.map((c) => ({ value: c.id, label: c.nameEn }))}
            required
            hint="Feed, medicine and fuel are not here: those are a Stock IN, not a cost."
          />
        ) : (
          <Select
            label="Where from"
            value={counterAccountCode}
            onChange={setCounterAccountCode}
            options={inOptions}
            required
            hint="A top-up from head office is a transfer, not a voucher."
          />
        )}
        <Field label="Payee" value={payee} onChange={setPayee} />
        <Field
          label="Paper voucher no"
          value={voucherNo}
          onChange={setVoucherNo}
          mono
          hint="The number written on the slip, if there is one."
        />
      </FormGroup>

      <FormGroup title="Note">
        <TextArea label="Note" value={narration} onChange={setNarration} />
      </FormGroup>

      {error && <ErrorNote message={error} />}

      <FormActions onCancel={() => setShowForm(false)}>
        <button
          type="button"
          className="btn btn--primary"
          disabled={mutate.isPending}
          onClick={() => void save()}
        >
          {editing ? 'Save changes' : 'Save voucher'}
        </button>
      </FormActions>
    </FormPanel>
  );

  return (
    <ScreenWithForm
      canCreate={can('fin.pettycash.create')}
      addLabel="Write a voucher"
      showForm={showForm}
      onToggle={() => {
        if (showForm) return setShowForm(false);
        reset();
        setShowForm(true);
      }}
      form={form}
    >
      <div className="toolbar toolbar--wrap">
        <Select
          label="Cash box"
          value={chosenBoxId}
          onChange={setCashBoxId}
          options={(boxes.data?.items ?? []).map((b) => ({
            value: b.id,
            label: `${b.nameEn} — ${b.farmName}`,
          }))}
          required
        />
        <Field label="From" type="date" value={from} onChange={setFrom} />
        <Field label="To" type="date" value={to} onChange={setTo} />
      </div>

      {note && <OkNote message={note} />}
      {error && !showForm && <ErrorNote message={error} />}

      <div className="tiles">
        <Tile
          label="Opening balance"
          value={formatMoney(page.data?.openingBalance ?? 0)}
          note={`Everything before ${formatDate(from)}`}
        />
        <Tile
          label="Cash in hand"
          value={formatMoney(page.data?.closingBalance ?? 0)}
          note={`As at ${formatDate(to)}`}
        />
      </div>

      <section className="panel">
        <h2>{page.data?.cashBoxName ?? 'The page'}</h2>
        <p className="muted">
          The running figure is worked out as this page is drawn, so a voucher written
          up for an earlier day corrects the whole column.
        </p>
        <Table
          count={page.data?.items.length ?? 0}
          empty="Nothing moved in or out over these days."
          head={
            <tr>
              <th>Date</th>
              <th>Entry</th>
              <th>What for</th>
              <th className="num">Money in</th>
              <th className="num">Money out</th>
              <th className="num">Cash in hand</th>
            </tr>
          }
        >
          {(page.data?.items ?? []).map((row, i) => (
            <tr key={`${row.entryNo}-${i}`}>
              <td>{formatDate(row.entryDate)}</td>
              <td>{row.sourceNo ?? row.entryNo}</td>
              <td>{row.narration}</td>
              <td className="num">{row.moneyIn === '0.00' ? '' : formatMoney(row.moneyIn)}</td>
              <td className="num">{row.moneyOut === '0.00' ? '' : formatMoney(row.moneyOut)}</td>
              <td className="num">{formatMoney(row.closingBalance)}</td>
            </tr>
          ))}
        </Table>
      </section>

      <ActionBar
        actions={[
          {
            label: 'Edit',
            icon: 'edit',
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.pettycash.update')),
            reason: 'Only a draft can be edited',
            onSelect: () => current && startEdit(current),
          },
          {
            label: 'Post',
            icon: 'post',
            primary: true,
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.pettycash.post')),
            reason: 'Only a draft can be posted',
            onSelect: () => current && void post(current),
          },
          {
            label: 'Delete draft',
            icon: 'remove',
            danger: true,
            enabled: Boolean(current && current.status === 'DRAFT' && can('fin.pettycash.delete')),
            reason: 'Only a draft can be deleted',
            onSelect: () => current && void remove(current),
          },
        ]}
      >
        {current?.status === 'POSTED' && can('fin.pettycash.reverse') && (
          <ReverseButton
            label={`Reverse ${current.documentNo}`}
            pending={mutate.isPending}
            onReverse={(reason) => {
              void mutate
                .mutateAsync(() =>
                  api.post(
                    `/api/v1/fin/cash-vouchers/${current.id}/reverse?version=${current.version}`,
                    { reason },
                  ),
                )
                .then(() => setNote('Voucher reversed.'))
                .catch((e: unknown) =>
                  setError(errorMessage(e, 'That voucher could not be reversed')),
                );
            }}
          />
        )}
      </ActionBar>

      <DataGrid
        id="fin-cash-vouchers"
        label="Cash vouchers"
        columns={columns}
        rows={rows}
        rowKey={(r) => r.id}
        selectedKey={selected}
        onSelect={(key) => setSelected(key)}
        onOpen={(row) => row.status === 'DRAFT' && startEdit(row)}
        empty="No vouchers for this cash box yet."
        loading={vouchers.isLoading}
      />

      <StatusBar>
        <span>
          {rows.length} voucher{rows.length === 1 ? '' : 's'}
        </span>
      </StatusBar>
    </ScreenWithForm>
  );
}
